import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTabSwipe } from '@/hooks/use-tab-swipe';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { ChevronLeft, Bell, BellRing, ImageOff, User } from 'lucide-react';
import { notify } from '@/utils/notifications';
import { useAppTheme } from '@/utils/theme-context';
import { useAuth } from '@pooflabs/web';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { Time, Address } from '@/lib/db-client';

import { subscribeUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { subscribeUserAvatars, UserAvatarsResponse } from '@/lib/collections/userAvatars';
import { getUsernames } from '@/lib/collections/usernames';
import { subscribeManyWallpapers, WallpapersResponse } from '@/lib/collections/wallpapers';
import { getManyPurchases, PurchasesResponse } from '@/lib/collections/purchases';
import {
  subscribeSubscriptionsCreators,
  setSubscriptionsCreators,
  deleteSubscriptionsCreators,
  SubscriptionsCreatorsResponse,
} from '@/lib/collections/subscriptions';
import { ProfileGridCard } from '@/components/ProfileGridCard';
import { WallpaperViewer } from '@/components/WallpaperViewer';
import { RingtoneFullScreenPlayer } from '@/components/RingtoneFullScreenPlayer';
import { MiniPlayer } from '@/components/MiniPlayer';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';
import { useRingtonePlayer, useRingtonePlayerState } from '@/contexts/RingtonePlayerContext';
import {
  subscribeManyUsersFavorites,
  subscribeManyUsersPurchases,
  setUsersFavorites,
  deleteUsersFavorites,
  UsersFavoritesResponse,
  UsersPurchasesResponse,
} from '@/lib/collections/users';

import { useBackButtonClose } from '@/hooks/use-back-button-close';
import { usePurchaseFlow } from '@/hooks/use-purchase-flow';

// Detect if a string looks like a Solana wallet address (base58, 32–44 chars)
function isSolanaAddress(str: string): boolean {
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(str);
}

// Shorten wallet to first4...last4
function shortWallet(addr: string): string {
  return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
}

type MediaFilter = 'all' | 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

const FILTER_CHIPS: { key: MediaFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'wallpaper', label: 'Wallpapers' },
  { key: 'live-wallpaper', label: 'Live' },
  { key: 'ringtone', label: 'Ringtones' },
  { key: 'icon', label: 'Icons' },
];

const FILTER_EMPTY_LABELS: Record<MediaFilter, string> = {
  all: 'content',
  wallpaper: 'wallpapers',
  'live-wallpaper': 'live wallpapers',
  ringtone: 'ringtones',
  icon: 'icons',
};

function resolveMediaType(w: WallpapersResponse): string {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored) return stored;
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  if (/\.(mp4|webm|mov|m4v)$/.test(url)) return 'live-wallpaper';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(url)) return 'ringtone';
  return 'wallpaper';
}

// ─── Ringtone row card for the profile grid ──────────────────────────────────
// Uses the same MiniPlayerBar component as FavoritesView and LibraryView.

interface ProfileRingtoneRowProps {
  wallpaper: WallpapersResponse;
  index: number;
  playlist: WallpapersResponse[];
  isFavorited: boolean;
  isPurchased: boolean;
  onToggleFavorite: (w: WallpapersResponse) => void;
}

const ProfileRingtoneRow: React.FC<ProfileRingtoneRowProps> = ({
  wallpaper,
  index,
  playlist,
  isFavorited,
  isPurchased,
  onToggleFavorite,
}) => {
  const {
    currentRingtone,
    isPlaying: globalPlaying,
    currentTime,
    duration: globalDuration,
    togglePlayPause,
    play,
    openFullScreen,
    setFavorited,
    setPurchased,
  } = useRingtonePlayer();

  const isThisTrack = currentRingtone?.id === wallpaper.id;
  // Show active state whenever this track is the current track and audio is playing.
  const isPlaying = globalPlaying && isThisTrack;
  const progress = isThisTrack && globalDuration > 0
    ? (currentTime / globalDuration) * 100
    : 0;

  const [duration, setDuration] = useState(0);
  const probeRef = useRef<HTMLAudioElement>(null);

  const handleProbeLoaded = () => {
    const a = probeRef.current;
    if (a && isFinite(a.duration)) setDuration(a.duration);
  };

  const durationSecs = duration > 0 ? Math.round(duration) : null;
  const category = (wallpaper as WallpapersResponse & { category?: string }).category ?? 'Audio';
  const subtitle = durationSecs != null ? `${category} • ${durationSecs}s` : category;

  // Keep player context in sync when the currently-loaded track's purchased state
  // changes (e.g. after a purchase completes while this track is already loaded).
  useEffect(() => {
    if (currentRingtone?.id === wallpaper.id) {
      setPurchased(isPurchased);
    }
  }, [isPurchased, currentRingtone?.id, wallpaper.id, setPurchased]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    togglePlayPause(wallpaper, playlist);
  };

  const handleBarClick = () => {
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    play(wallpaper, playlist);
    openFullScreen();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      style={{ width: '100%' }}
    >
      <audio
        ref={probeRef}
        src={wallpaper.imageUrl ?? ''}
        preload="metadata"
        onLoadedMetadata={handleProbeLoaded}
        style={{ display: 'none' }}
      />
      <MiniPlayerBar
        title={wallpaper.title ?? 'Untitled'}
        subtitle={subtitle}
        isPlaying={isPlaying}
        progress={progress}
        onPlayPause={togglePlay}
        showNextButton={false}
        showDismissButton={false}
        onBarClick={handleBarClick}
        showFavoriteButton
        isFavorited={isFavorited}
        onToggleFavorite={(e) => { e.stopPropagation(); onToggleFavorite(wallpaper); }}
      />
    </motion.div>
  );
};

// ─── Inner profile display once we have walletAddress ───────────────────────

interface ProfileContentProps {
  walletAddress: string;
}

const ProfileContent: React.FC<ProfileContentProps> = ({ walletAddress }) => {
  const navigate = useNavigate();
  const { tokens } = useAppTheme();
  const { user, loading: authLoading } = useAuth();
  const myAddress = user?.address ?? null;
  const isOwnProfile = !!myAddress && myAddress.toLowerCase() === walletAddress.toLowerCase();

  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');

  const [selectedWallpaper, setSelectedWallpaper] = useState<WallpapersResponse | null>(null);
  const [viewerHandoffActive, setViewerHandoffActive] = useState(false);
  const ringtonePlayer = useRingtonePlayer();
  const { currentRingtone, floatingPlayerEnabled, fullScreenOpen } = useRingtonePlayerState();
  // Mini player is visible when a track is loaded, the floating player is enabled,
  // and the full-screen view is not open. When visible it needs extra bottom clearance
  // so it doesn't overlap the last row of the content grid.
  const isMiniPlayerVisible = !!currentRingtone && floatingPlayerEnabled && !fullScreenOpen;

  // ── Scroll-collapse hero transforms (lighter variant: 80→40px avatar) ────
  const { scrollY } = useScroll();
  const SCROLL_RANGE: [number, number] = [0, 120];

  const rawAvatarSize = useTransform(scrollY, SCROLL_RANGE, [80, 40]);
  const avatarSize = useSpring(rawAvatarSize, { stiffness: 100, damping: 20 });

  const rawSecondaryOpacity = useTransform(scrollY, SCROLL_RANGE, [1, 0]);
  const secondaryOpacity = useSpring(rawSecondaryOpacity, { stiffness: 100, damping: 20 });

  const rawHeroPaddingTop = useTransform(scrollY, SCROLL_RANGE, [56, 10]);
  const heroPaddingTop = useSpring(rawHeroPaddingTop, { stiffness: 100, damping: 20 });

  const rawHeroPaddingBottom = useTransform(scrollY, SCROLL_RANGE, [16, 8]);
  const heroPaddingBottom = useSpring(rawHeroPaddingBottom, { stiffness: 100, damping: 20 });

  const rawHeroGap = useTransform(scrollY, SCROLL_RANGE, [12, 4]);
  const heroGap = useSpring(rawHeroGap, { stiffness: 100, damping: 20 });

  const rawUsernameSize = useTransform(scrollY, SCROLL_RANGE, [20, 16]);
  const usernameSize = useSpring(rawUsernameSize, { stiffness: 100, damping: 20 });
  const [subLoading, setSubLoading] = useState(false);
  const [optimisticSub, setOptimisticSub] = useState<boolean | null>(null);

  // Android hardware back button: close overlays before leaving the profile page.
  // useBackButtonClose pushes a sentinel when the overlay opens and pops it on
  // programmatic close, so the history stack stays clean when the user navigates
  // away — pressing back will return to the previous route, not get stuck on
  // orphaned sentinel entries.
  //
  // markNavigating() is called during cross-viewer handoffs (wallpaper ↔ ringtone)
  // to prevent the cleanup from calling history.back() and accidentally popping
  // the freshly-pushed sentinel for the incoming viewer.
  const { markNavigating: markViewerHandoff } = useBackButtonClose(!!selectedWallpaper, () => setSelectedWallpaper(null), 'profile-viewer');
  const { markNavigating: markRingtoneHandoff } = useBackButtonClose(fullScreenOpen, () => ringtonePlayer.closeFullScreen(), 'profile-ringtone-fullscreen');
  // Global useExploreGuard in App.tsx handles navigating back to Explore when
  // no overlays are open. useBackButtonClose above handles the overlay back presses.

  // ── Data subscriptions ──────────────────────────────────────────────────────
  const { data: profile, loading: profileLoading } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!walletAddress,
    walletAddress,
  );

  const { data: avatarData } = useRealtimeData<UserAvatarsResponse | null>(
    subscribeUserAvatars,
    !!walletAddress,
    walletAddress,
  );

  // Subscription state: is current user subscribed to this creator?
  const { data: subscriptionData } = useRealtimeData<SubscriptionsCreatorsResponse | null>(
    subscribeSubscriptionsCreators,
    !!myAddress && !isOwnProfile && !!walletAddress,
    myAddress ?? '',
    walletAddress,
  );

  const isSubscribed = optimisticSub !== null ? optimisticSub : !!subscriptionData;

  // Creator's published wallpapers
  const { data: allWallpapers } = useRealtimeData<WallpapersResponse[]>(
    subscribeManyWallpapers,
    !!walletAddress,
    `where uploadedBy = '${walletAddress}' and status = 'published'`,
  );

  const publishedWallpapers = useMemo(
    () => (allWallpapers ?? []).filter((w) => w.status === 'published'),
    [allWallpapers],
  );

  // Compute which category tabs to show based on actual creator content.
  // A tab is shown only when the creator has at least one item of that type.
  // The "All" tab is shown only when 2+ categories are non-empty.
  const visibleChips = useMemo(() => {
    const items = publishedWallpapers ?? [];
    const nonEmptyKeys = new Set(items.map((w) => resolveMediaType(w) as MediaFilter));
    const contentChips = FILTER_CHIPS.filter(
      (chip) => chip.key !== 'all' && nonEmptyKeys.has(chip.key),
    );
    // Include "All" tab only when there are 2+ distinct non-empty categories
    if (contentChips.length >= 2) {
      return [FILTER_CHIPS[0], ...contentChips]; // FILTER_CHIPS[0] is 'all'
    }
    return contentChips;
  }, [publishedWallpapers]);

  // When visible tabs change, ensure the selected tab is still valid.
  // If "all" was selected but is no longer visible (< 2 categories), fall back
  // to the first visible tab. If no tabs are visible yet, keep current selection.
  useEffect(() => {
    if (visibleChips.length === 0) return;
    const isCurrentVisible = visibleChips.some((c) => c.key === mediaFilter);
    if (!isCurrentVisible) {
      setMediaFilter(visibleChips[0].key);
    }
  }, [visibleChips, mediaFilter]);

  // Swipe-to-switch filter — driven by visibleChips so swipe only cycles shown tabs
  const filterChipKeys = visibleChips.map((c) => c.key);
  const activeFilterIndex = filterChipKeys.indexOf(mediaFilter);
  const { swipeCallbackRef: filterSwipeRef } = useTabSwipe({
    count: filterChipKeys.length,
    activeIndex: activeFilterIndex < 0 ? 0 : activeFilterIndex,
    onIndexChange: (i) => setMediaFilter(filterChipKeys[i]),
  });

  // For current user: purchases and favorites (for card state)
  const { data: myPurchases } = useRealtimeData<UsersPurchasesResponse[]>(
    subscribeManyUsersPurchases,
    !!myAddress,
    myAddress ?? '',
  );

  const { data: myFavorites } = useRealtimeData<UsersFavoritesResponse[]>(
    subscribeManyUsersFavorites,
    !!myAddress,
    myAddress ?? '',
  );

  const purchasedSet = useMemo(
    () => new Set((myPurchases ?? []).map((p) => p.wallpaperId)),
    [myPurchases],
  );

  const favoritedSet = useMemo(
    () => new Set((myFavorites ?? []).map((f) => f.id)),
    [myFavorites],
  );

  // Keep the ringtone player context in sync with the live purchased/favorited
  // state for the currently-loaded track. Without this, navigating to a profile
  // page with a ringtone already loaded in the player won't reflect purchase state.
  useEffect(() => {
    const id = ringtonePlayer.currentRingtone?.id;
    if (!id) return;
    ringtonePlayer.setPurchased(purchasedSet.has(id));
    ringtonePlayer.setFavorited(favoritedSet.has(id));
  }, [ringtonePlayer.currentRingtone?.id, purchasedSet, favoritedSet, ringtonePlayer.setPurchased, ringtonePlayer.setFavorited]);

  // Shared purchase flow — same logic that powers Buy on the home feed.
  const {
    skrBalance,
    purchasingId,
    handlePurchase,
  } = usePurchaseFlow({ purchasedIds: purchasedSet });

  const handleToggleFavorite = async (wallpaper: WallpapersResponse) => {
    if (!myAddress) {
      notify.error('Connect your wallet to save favorites', myAddress);
      return;
    }
    if (favoritedSet.has(wallpaper.id)) {
      const ok = await deleteUsersFavorites(myAddress, wallpaper.id);
      if (!ok) notify.error('Failed to update favorite', myAddress);
    } else {
      const ok = await setUsersFavorites(myAddress, wallpaper.id, {
        wallpaperId: wallpaper.id,
        owner: Address.publicKey(myAddress),
        createdAt: Time.Now,
      });
      if (!ok) notify.error('Failed to update favorite', myAddress);
    }
  };

  // Stats: load purchases for computing sales (client-side)
  const [purchases, setPurchases] = useState<PurchasesResponse[]>([]);
  useEffect(() => {
    if (!walletAddress || publishedWallpapers.length === 0) {
      setPurchases([]);
      return;
    }
    let cancelled = false;
    getManyPurchases().then((all) => {
      if (!cancelled) setPurchases(all);
    });
    return () => { cancelled = true; };
  }, [walletAddress, publishedWallpapers.length]);

  const stats = useMemo(() => {
    const uploads = publishedWallpapers.length;
    const downloads = publishedWallpapers.reduce((s, w) => s + (w.downloadCount ?? 0), 0);
    const wallpaperIds = new Set(publishedWallpapers.map((w) => w.id));
    const sales = purchases.filter((p) => wallpaperIds.has(p.wallpaperId)).length;
    return { uploads, downloads, sales };
  }, [publishedWallpapers, purchases]);

  // ── Filtered + sorted wallpapers (newest first) ──────────────────────────────
  const filteredWallpapers = useMemo(() => {
    const base = mediaFilter === 'all'
      ? publishedWallpapers
      : publishedWallpapers.filter((w) => resolveMediaType(w) === mediaFilter);
    // Sort chronologically: newest upload date first
    return [...base].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }, [publishedWallpapers, mediaFilter]);

  // ── Display name ────────────────────────────────────────────────────────────
  const username = profile?.username ?? null;
  const displayName = username ? `@${username}` : shortWallet(walletAddress);
  const avatarUrl = avatarData?.avatarUrl ?? null;

  // ── Creator not found: non-owner viewing a deleted/non-existent profile ─────
  // After loading completes, if profile is null and it's not the viewer's own address,
  // show a "Creator not found" empty state. The viewer's own profile will still render
  // (they see their profile page which may prompt them to set up again via ProfileSetupGate).
  const profileDefinitelyGone = !profileLoading && !authLoading && profile === null && !isOwnProfile;

  // ── Subscribe toggle ─────────────────────────────────────────────────────────
  const handleToggleSub = async () => {
    if (!myAddress) {
      notify.error('Connect your wallet to subscribe', myAddress);
      return;
    }
    if (isOwnProfile) return;
    setSubLoading(true);
    const next = !isSubscribed;
    setOptimisticSub(next);
    let ok: boolean;
    if (next) {
      ok = await setSubscriptionsCreators(myAddress, walletAddress, {
        subscriber: Address.publicKey(myAddress),
        creator: Address.publicKey(walletAddress),
        createdAt: Time.Now,
      });
    } else {
      ok = await deleteSubscriptionsCreators(myAddress, walletAddress);
    }
    if (!ok) {
      setOptimisticSub(!next);
      notify.error('Failed to update subscription', myAddress);
    }
    setSubLoading(false);
    // After a brief delay, clear optimistic override so live data takes over
    setTimeout(() => setOptimisticSub(null), 1500);
  };

  if (profileDefinitelyGone) {
    return (
      <div style={{
        minHeight: '100vh', background: tokens.bg, color: tokens.textPrimary,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 12, fontFamily: "'Syne', sans-serif",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            position: 'absolute', top: 14, left: 14,
            width: 36, height: 36, borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: tokens.textMuted,
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = tokens.bgHover;
            (e.currentTarget as HTMLElement).style.color = tokens.textPrimary;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.color = tokens.textMuted;
          }}
        >
          <ChevronLeft size={22} strokeWidth={1.6} />
        </button>
        <User size={48} color={tokens.accentMuted} strokeWidth={1.2} />
        <p style={{ fontSize: 18, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Creator not found</p>
        <p style={{ fontSize: 13, color: tokens.textVeryMuted, margin: 0, textAlign: 'center', maxWidth: 260 }}>
          This creator profile no longer exists or has been deleted.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: tokens.bg,
        color: tokens.textPrimary,
        fontFamily: "'Syne', sans-serif",
      }}
    >
      {/* ── Scroll-collapse sticky hero (lighter variant) ──────────────── */}
      <motion.div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: tokens.bgHeader,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          paddingTop: heroPaddingTop,
          paddingBottom: heroPaddingBottom,
          paddingLeft: 12,
          paddingRight: 12,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: heroGap,
        }}
      >
        {/* Back button — always top-left */}
        <div style={{
          position: 'absolute', top: 8, left: 8,
          display: 'flex', alignItems: 'center',
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'transparent',
              border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: tokens.textMuted,
              flexShrink: 0,
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = tokens.bgHover;
              (e.currentTarget as HTMLElement).style.color = tokens.textPrimary;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = tokens.textMuted;
            }}
          >
            <ChevronLeft size={22} strokeWidth={1.6} />
          </button>
        </div>

        {/* Avatar row — avatar shrinks 80→40, sits beside name when compact */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
          {/* Avatar — shrinks 80→40 */}
          <motion.div
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: '50%',
              background: 'transparent',
              padding: 0,
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: avatarUrl ? tokens.accentMuted : tokens.bgCardSubtle,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <User size={24} color={tokens.textMuted} strokeWidth={1.5} />
              )}
            </div>
          </motion.div>

          {/* Name column — font shrinks 20→16, always visible */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <motion.h1
                style={{
                  fontSize: displayName.length <= 8 ? 22 : displayName.length <= 11 ? 19 : 16,
                  fontWeight: 700,
                  color: tokens.textPrimary,
                  margin: 0,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  fontFamily: username ? "'Syne', sans-serif" : "'DM Mono', monospace",
                }}
              >
                {displayName}
              </motion.h1>
            </div>

            {/* Subscribe button — always visible, persists in compact bar */}
            {!isOwnProfile && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleToggleSub}
                disabled={subLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 14px',
                  borderRadius: 50,
                  background: isSubscribed ? tokens.accent : 'transparent',
                  border: isSubscribed
                    ? `1px solid ${tokens.accent}99`
                    : `1px solid ${tokens.border}`,
                  color: isSubscribed ? '#fff' : tokens.textMuted,
                  cursor: 'pointer',
                  fontSize: 12, fontWeight: 600,
                  fontFamily: "'Syne', sans-serif",
                  transition: 'all 0.2s ease',
                  opacity: subLoading ? 0.7 : 1,
                }}
              >
                {isSubscribed ? (
                  <>
                    <BellRing size={13} />
                    Notifying
                  </>
                ) : (
                  <>
                    <Bell size={13} />
                    Notify
                  </>
                )}
              </motion.button>
            )}
          </div>
        </div>

        {/* Stats row — fades out on scroll */}
        <motion.div
          style={{
            opacity: secondaryOpacity,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 13, color: tokens.textMuted,
            fontFamily: "'DM Mono', monospace",
            flexWrap: 'wrap', justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <span><strong style={{ color: tokens.textPrimary, fontFamily: "'Syne', sans-serif" }}>{stats.uploads}</strong> uploads</span>
          <span style={{ color: tokens.textVeryMuted }}>•</span>
          <span><strong style={{ color: tokens.textPrimary, fontFamily: "'Syne', sans-serif" }}>{stats.downloads}</strong> downloads</span>
          <span style={{ color: tokens.textVeryMuted }}>•</span>
          <span><strong style={{ color: tokens.textPrimary, fontFamily: "'Syne', sans-serif" }}>{stats.sales}</strong> sales</span>
        </motion.div>

      </motion.div>

      {/* ── Swipe container: filter chips + content grid ────────────────────── */}
      <div
        ref={filterSwipeRef as React.RefCallback<HTMLDivElement>}
        style={{ overflow: 'hidden', touchAction: 'pan-y' }}
      >
      {/* ── Filter chips ────────────────────────────────────────────────────── */}
      <div
        data-no-swipe
        style={{
        padding: '7px 16px 16px',
        overflowX: 'auto',
        display: 'flex', gap: 8,
        scrollbarWidth: 'none',
      }}>
        {visibleChips.map((chip) => (
          <button
            key={chip.key}
            onClick={() => setMediaFilter(chip.key)}
            style={{
              flexShrink: 0,
              padding: '6px 14px',
              borderRadius: 50,
              background: mediaFilter === chip.key
                ? tokens.accent
                : tokens.bgCard,
              border: mediaFilter === chip.key
                ? `1px solid ${tokens.accent}99`
                : `1px solid ${tokens.border}`,
              color: mediaFilter === chip.key ? '#fff' : tokens.textMuted,
              fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              fontFamily: "'Syne', sans-serif",
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* ── Content grid ────────────────────────────────────────────────────── */}
      {/* Use a fixed bottom padding + an animated spacer to avoid repainting padding changes */}
      <div style={{ padding: '0 7px 80px' }}>
        {filteredWallpapers.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '60px 24px',
            gap: 12, color: tokens.textVeryMuted, textAlign: 'center',
          }}>
            <ImageOff size={40} strokeWidth={1.2} color={tokens.textVeryMuted} />
            <p style={{ fontSize: 15, fontWeight: 600, color: tokens.textVeryMuted, margin: 0 }}>
              {publishedWallpapers.length === 0
                ? 'No content published yet'
                : `No ${FILTER_EMPTY_LABELS[mediaFilter]} yet`}
            </p>
          </div>
        ) : (() => {
          // Build mixed layout: visual items (wallpapers + live) fill a continuous
          // 3-column grid; ringtone rows are full-width and inserted only at row
          // boundaries so they never split a visual row or force a lone card to
          // stretch full-width.
          //
          // Grid uses 6 columns:
          //   visual card  → always span 2  (3 × 2 = 6 cols, fixed, never changes)
          //   ringtone row → gridColumn 1 / -1 (full width)
          //
          // Algorithm: walk filteredWallpapers maintaining a visual-row buffer
          // (max 3 items). When a ringtone appears, first drain the row buffer
          // into the output (emitting however many visual cards are buffered —
          // 1, 2, or 3 — all at span 2), then emit the ringtone full-width.
          // Consecutive ringtones each get their own full-width row.
          // At the end, flush any remaining buffered visual cards.
          const gridCols = 6;
          const colCount = 3;
          const visualSpan = Math.floor(gridCols / colCount); // always 2

          const rows: React.ReactNode[] = [];
          // Buffer for visual items that haven't been emitted yet (< colCount).
          let visualBuffer: typeof filteredWallpapers = [];
          // Global visual index (used for playlist position on visual items).
          // We track the original index in filteredWallpapers for each item.

          const flushVisualBuffer = () => {
            // For a partial trailing row (fewer than colCount items), compute a
            // wider span so items stretch to fill the full row width:
            //   1 item  → span 6 (full width)
            //   2 items → span 3 (half each)
            //   3 items → span 2 (normal, 3 × 2 = 6)
            const count = visualBuffer.length;
            const rowSpan = count > 0 ? Math.floor(gridCols / count) : visualSpan;

            for (const item of visualBuffer) {
              const idx = filteredWallpapers.indexOf(item);
              rows.push(
                <div
                  key={item.id}
                  style={{
                    gridColumn: `span ${rowSpan}`,
                    minWidth: 0,
                  }}
                >
                  <ProfileGridCard
                    wallpaper={item}
                    index={idx}
                    onClick={() => setSelectedWallpaper(item)}
                  />
                </div>
              );
            }
            visualBuffer = [];
          };

          for (let i = 0; i < filteredWallpapers.length; i++) {
            const w = filteredWallpapers[i];
            const type = resolveMediaType(w);

            if (type === 'ringtone') {
              // Finish the current visual row (emit whatever is buffered) before
              // the ringtone so it never splits mid-row.
              flushVisualBuffer();
              // Full-width ringtone row
              rows.push(
                <div
                  key={w.id}
                  style={{
                    gridColumn: `1 / -1`,
                    marginBottom: 2,
                  }}
                >
                  <ProfileRingtoneRow
                    wallpaper={w}
                    index={i}
                    playlist={filteredWallpapers}
                    isFavorited={favoritedSet.has(w.id)}
                    isPurchased={purchasedSet.has(w.id)}
                    onToggleFavorite={handleToggleFavorite}
                  />
                </div>
              );
            } else {
              // Accumulate visual items; emit a complete row of 3 when ready.
              visualBuffer.push(w);
              if (visualBuffer.length === colCount) {
                flushVisualBuffer();
              }
            }
          }
          // Emit any remaining visual items (partial trailing row — spans adjusted to fill width).
          flushVisualBuffer();

          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                gap: 6,
              }}
            >
              {rows}
            </div>
          );
        })()}
      </div>
      </div>{/* end swipe container */}
      {/* GPU-composited spacer: animates via transform instead of padding reflow */}
      <div
        style={{
          height: 20,
          transform: `translateY(${isMiniPlayerVisible ? 20 : 0}px)`,
          transition: 'transform 0.3s ease',
          willChange: 'transform',
          pointerEvents: 'none',
        }}
      />

      {/* ── Wallpaper viewer ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {selectedWallpaper && (
          <WallpaperViewer
            wallpaper={selectedWallpaper}
            onClose={() => setSelectedWallpaper(null)}
            isPurchased={purchasedSet.has(selectedWallpaper.id)}
            isFavorited={favoritedSet.has(selectedWallpaper.id)}
            onPurchase={handlePurchase}
            onToggleFavorite={handleToggleFavorite}
            purchasing={purchasingId === selectedWallpaper.id}
            skrBalance={skrBalance}
            wallpapersList={filteredWallpapers}
            onNavigate={(w) => {
              if (resolveMediaType(w) === 'ringtone') {
                // Swipe landed on a ringtone — hand off to RingtoneFullScreenPlayer.
                // markViewerHandoff() prevents the useBackButtonClose cleanup from
                // calling history.back(), which would race with the ringtone player's
                // freshly-pushed sentinel and immediately close it.
                markViewerHandoff();
                setViewerHandoffActive(true);
                setSelectedWallpaper(null);
                ringtonePlayer.play(w, filteredWallpapers);
                ringtonePlayer.openFullScreen();
                requestAnimationFrame(() => setViewerHandoffActive(false));
              } else {
                setSelectedWallpaper(w);
              }
            }}
            instantHandoff={viewerHandoffActive}
          />
        )}
      </AnimatePresence>

      {/* Full-screen ringtone player */}
      <RingtoneFullScreenPlayer
        onToggleFavorite={handleToggleFavorite}
        onPurchase={handlePurchase}
        onTrash={() => {}}
        purchasing={purchasingId !== null && purchasingId === ringtonePlayer.currentRingtone?.id}
        instantHandoff={viewerHandoffActive}
        onAdvanceToVisual={(item) => {
          // Swipe from ringtone to a visual item (wallpaper/live wallpaper).
          // markRingtoneHandoff() prevents the useBackButtonClose cleanup from
          // calling history.back(), which would race with the wallpaper viewer's
          // freshly-pushed sentinel and immediately close it.
          markRingtoneHandoff();
          setViewerHandoffActive(true);
          ringtonePlayer.closeFullScreen();
          ringtonePlayer.stop();
          setSelectedWallpaper(item);
          requestAnimationFrame(() => setViewerHandoffActive(false));
        }}
      />

      {/* Floating mini-player — shifted down on this page so it sits near the bottom edge instead of overlapping content */}
      <MiniPlayer bottomOffsetPx={16} />
    </div>
  );
};

// ─── Outer component: resolves identifier → walletAddress ────────────────────

const UserProfilePage: React.FC = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const [resolvedWallet, setResolvedWallet] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { tokens } = useAppTheme();

  useEffect(() => {
    if (!identifier) {
      setNotFound(true);
      setResolving(false);
      return;
    }

    if (isSolanaAddress(identifier)) {
      // Direct wallet address
      setResolvedWallet(identifier);
      setResolving(false);
    } else {
      // Username lookup via usernames/<lowercase>
      setResolving(true);
      getUsernames(identifier.toLowerCase()).then((doc) => {
        if (doc?.owner) {
          setResolvedWallet(doc.owner);
        } else {
          setNotFound(true);
        }
        setResolving(false);
      });
    }
  }, [identifier]);

  if (resolving) {
    return (
      <div style={{
        minHeight: '100vh', background: tokens.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          border: `2px solid ${tokens.accentMuted}`,
          borderTopColor: tokens.accent,
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (notFound || !resolvedWallet) {
    return (
      <div style={{
        minHeight: '100vh', background: tokens.bg, color: tokens.textPrimary,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 12, fontFamily: "'Syne', sans-serif",
      }}>
        <User size={48} color={tokens.accent} strokeWidth={1.2} />
        <p style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>User not found</p>
        <p style={{ fontSize: 13, color: tokens.textVeryMuted, margin: 0 }}>
          No profile exists for <strong style={{ color: tokens.textMuted }}>{identifier}</strong>
        </p>
      </div>
    );
  }

  return <ProfileContent walletAddress={resolvedWallet} />;
};

export default UserProfilePage;
